import {
  CashSession,
  CashSessionConfirmAction,
  CashTransaction,
} from '@/@types/cash-management';
import { ConfirmModal, ConfirmPrintModal } from '@/components/common';
import {
  useCashSession,
  useGetCashSession,
} from '@/modules/cash_management/hooks';
import { useGetCurrentCashSession } from '@/modules/cash_management/hooks/useGetCurrentCashSession';
import { useCan } from '@/modules/core/hooks/useCan';
import { usePrintControls } from '@/modules/core/hooks';
import { useDocumentTypes } from '@/modules/document_types/hooks';
import { useSettings } from '@/modules/settings/hooks';
import { isBeforeToday, parseErrorMessage } from '@/utils';
import {
  Add,
  AssignmentTurnedIn,
  CheckCircle,
  KeyboardArrowDown,
  LockOutlined,
  SwapHoriz,
  SvgIconComponent,
  Visibility,
  Warning,
} from '@mui/icons-material';
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { MouseEvent, useState } from 'react';
import { CashSessionDetailModal } from '../CashSessionDetailModal';
import { cashSessionStatusColor } from '../CashSessionStatusLabel';
import { useCashSessionManualMovementPrint } from '../useCashSessionManualMovementPrint';
import {
  CashSessionSummaryPrintMethod,
  useCashSessionSummaryPrint,
} from '../useCashSessionSummaryPrint';
import {
  ManualMovementFormData,
  ManualMovementModal,
} from '../ManualMovementModal';
import {
  OpenCashSessionFormData,
  OpenCashSessionModal,
} from '../OpenCashSessionModal';
import {
  ReconciliationDeclarationsFormData,
  ReconciliationDeclarationsModal,
} from '../ReconciliationDeclarationsModal';
import {
  cashSessionConfirmActionConfig,
  cashSessionConfirmModalConfig,
} from '../cash-management.utils';

type CashSessionAction = {
  key: string;
  label: string;
  icon: SvgIconComponent;
  onClick: () => void;
};

type PrintConfirmAction = 'closed-summary' | 'manual-movement';

function getConfirmActionFallbackMessage(action: CashSessionConfirmAction) {
  if (action === 'start-reconciliation') {
    return 'Não foi possível iniciar a conferência';
  }

  if (action === 'close') {
    return 'Não foi possível fechar o caixa';
  }

  if (action === 'cancel-manual-movement') {
    return 'Não foi possível cancelar a movimentação';
  }

  return 'Não foi possível fechar o caixa';
}

export function CashSessionActionsButton() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOpenSessionModalOpen, setIsOpenSessionModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isManualMovementModalOpen, setIsManualMovementModalOpen] =
    useState(false);
  const [isDeclarationsModalOpen, setIsDeclarationsModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] =
    useState<CashSessionConfirmAction>();
  const [selectedCashSessionId, setSelectedCashSessionId] = useState<string>();
  const [reconciliationCashSession, setReconciliationCashSession] =
    useState<CashSession>();
  const [manualMovementTransactionId, setManualMovementTransactionId] =
    useState<string>();
  const [printConfirmAction, setPrintConfirmAction] =
    useState<PrintConfirmAction>();
  const [closedCashSessionId, setClosedCashSessionId] = useState<string>();
  const [manualMovementPrintTransaction, setManualMovementPrintTransaction] =
    useState<CashTransaction>();

  const { enqueueSnackbar } = useSnackbar();
  const currentCashSessionQuery = useGetCurrentCashSession();
  const currentCashSession = currentCashSessionQuery.data;
  const openCashSession =
    currentCashSession?.status === 'OPEN' ? currentCashSession : undefined;
  const canOpen = useCan({ permissions: ['cash_sessions_open'] });
  const canViewDetails = useCan({ permissions: ['cash_sessions_details'] });
  const canCreateManualMovement = useCan({
    permissions: ['cash_sessions_manual_movement'],
  });
  const canCancelManualMovement = useCan({
    permissions: ['cash_sessions_cancel_manual_movement'],
  });
  const canReconcile = useCan({
    permissions: ['cash_sessions_reconciliation'],
  });
  const canPrintSummary = useCan({ permissions: ['cash_sessions_summary'] });
  const cashSessionDetailQuery = useGetCashSession({
    id: selectedCashSessionId || openCashSession?.id,
    enabled:
      isDetailModalOpen ||
      (canViewDetails && isDeclarationsModalOpen && !reconciliationCashSession),
  });
  const documentTypesQuery = useDocumentTypes({
    sortBy: 'name',
    sort: 'asc',
    enabled: isManualMovementModalOpen || isDeclarationsModalOpen,
  });
  const settingsQuery = useSettings({ enabled: isManualMovementModalOpen });
  const cashSession = useCashSession();
  const { isPrintingSummary, printCashSessionSummary, printSummaryNode } =
    useCashSessionSummaryPrint();
  const {
    isPrintingManualMovement,
    printManualMovement,
    printManualMovementNode,
  } = useCashSessionManualMovementPrint();
  const {
    copies,
    handleCopiesChange,
    handleLayoutChange,
    handlePrintWidthChange,
    handlePrintingChange,
    isPrinting,
    selectedLayout,
    selectedPrintWidth,
  } = usePrintControls();

  const isMenuOpen = Boolean(anchorEl);
  const label = openCashSession ? 'Caixa Aberto' : 'Abrir Caixa';
  const isOpenedBeforeToday = Boolean(
    openCashSession?.openedAt && isBeforeToday(openCashSession.openedAt)
  );
  const getButtonColor = () => {
    if (!openCashSession) return 'error';

    if (isOpenedBeforeToday) {
      return 'warning';
    }

    return cashSessionStatusColor.OPEN;
  };
  const buttonColor = getButtonColor();
  const getStatusIcon = (): SvgIconComponent => {
    if (!openCashSession) return LockOutlined;
    if (isOpenedBeforeToday) return Warning;

    return CheckCircle;
  };
  const StatusIcon = getStatusIcon();
  const getStatusIconColor = () => {
    if (!openCashSession) return 'error' as const;
    if (isOpenedBeforeToday) return 'warning' as const;

    return 'success' as const;
  };
  const statusIconColor = getStatusIconColor();
  const detailCashSession = cashSessionDetailQuery.data || openCashSession;
  const declarationsCashSession =
    reconciliationCashSession || cashSessionDetailQuery.data;

  const handleSuccess = (message: string, callback?: () => void) => {
    enqueueSnackbar(message, {
      variant: 'success',
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    });
    callback?.();
  };

  const handleError = (error: unknown, fallbackMessage: string) => {
    enqueueSnackbar(parseErrorMessage(error, fallbackMessage), {
      variant: 'error',
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    });
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const refetchCashSessionDetailIfAllowed = async () => {
    if (!canViewDetails || !isDetailModalOpen) return;

    await cashSessionDetailQuery.refetch();
  };

  const handleOpenCashSession = async (data: OpenCashSessionFormData) => {
    try {
      await cashSession.openCashSessionMutation.mutateAsync(data);
      await currentCashSessionQuery.refetch();
      handleSuccess('Caixa aberto com sucesso', () =>
        setIsOpenSessionModalOpen(false)
      );
    } catch (error) {
      handleError(error, 'Não foi possível abrir o caixa');
    }
  };

  const handleCreateManualMovement = async (data: ManualMovementFormData) => {
    try {
      const createdManualMovement =
        await cashSession.createManualMovementMutation.mutateAsync(data);

      handleSuccess('Movimentação manual registrada com sucesso', () => {
        setIsManualMovementModalOpen(false);
        if (createdManualMovement.cashSession) {
          setManualMovementPrintTransaction(createdManualMovement);
          setPrintConfirmAction('manual-movement');
        }
      });
    } catch (error) {
      handleError(error, 'Não foi possível registrar a movimentação manual');
    }
  };

  const handleSaveDeclarations = async (
    data: ReconciliationDeclarationsFormData
  ) => {
    const cashSessionId = selectedCashSessionId || openCashSession?.id;

    if (!cashSessionId) return;

    try {
      const updatedCashSession =
        await cashSession.updateDeclarationsMutation.mutateAsync({
          id: cashSessionId,
          payload: data,
        });
      setReconciliationCashSession(updatedCashSession);
      await currentCashSessionQuery.refetch();
      handleSuccess('Conferência salva com sucesso', () => {
        setIsDeclarationsModalOpen(false);
        setConfirmAction('close');
      });
    } catch (error) {
      handleError(error, 'Não foi possível salvar a conferência');
    }
  };

  const handleConfirmAction = async () => {
    const cashSessionId = selectedCashSessionId || openCashSession?.id;

    if (!cashSessionId || !confirmAction) return;

    try {
      if (confirmAction === 'start-reconciliation') {
        const reconciledCashSession =
          await cashSession.startReconciliationMutation.mutateAsync(
            cashSessionId
          );
        setReconciliationCashSession(reconciledCashSession);
        await currentCashSessionQuery.refetch();
        handleSuccess('Conferência iniciada com sucesso', () => {
          setConfirmAction(undefined);
          setIsDeclarationsModalOpen(true);
        });
        return;
      }

      if (confirmAction === 'close') {
        const closedCashSession =
          await cashSession.closeCashSessionMutation.mutateAsync(cashSessionId);
        await currentCashSessionQuery.refetch();
        handleSuccess('Caixa fechado com sucesso', () => {
          setIsDeclarationsModalOpen(false);
          setSelectedCashSessionId(undefined);
          setReconciliationCashSession(undefined);
          if (canPrintSummary) {
            setClosedCashSessionId(closedCashSession.id);
            setPrintConfirmAction('closed-summary');
          }
        });
      }

      if (confirmAction === 'cancel-manual-movement') {
        if (!manualMovementTransactionId) return;

        await cashSession.cancelManualMovementMutation.mutateAsync(
          manualMovementTransactionId
        );
        await Promise.all([
          currentCashSessionQuery.refetch(),
          refetchCashSessionDetailIfAllowed(),
        ]);
        handleSuccess('Movimentação manual cancelada com sucesso', () => {
          setManualMovementTransactionId(undefined);
        });
      }

      setConfirmAction(undefined);
    } catch (error) {
      const fallbackMessage = getConfirmActionFallbackMessage(confirmAction);

      handleError(error, fallbackMessage);
    }
  };

  const handleClosePrintConfirm = () => {
    setPrintConfirmAction(undefined);
    setClosedCashSessionId(undefined);
    setManualMovementPrintTransaction(undefined);
    handlePrintingChange(false);
    handleCopiesChange(1);
  };

  const handleConfirmPrint = (
    method: CashSessionSummaryPrintMethod = 'browser'
  ) => {
    if (printConfirmAction === 'closed-summary' && closedCashSessionId) {
      handlePrintingChange(true);
      printCashSessionSummary(
        closedCashSessionId,
        'pos',
        copies,
        selectedLayout,
        selectedPrintWidth,
        method
      );
      handleClosePrintConfirm();
      return;
    }

    if (
      printConfirmAction === 'manual-movement' &&
      manualMovementPrintTransaction
    ) {
      handlePrintingChange(true);
      printManualMovement(
        manualMovementPrintTransaction,
        copies,
        selectedLayout
      );
      handleClosePrintConfirm();
    }
  };

  const actions: CashSessionAction[] = [];

  if (currentCashSession === null && canOpen) {
    actions.push({
      key: 'open',
      label: 'Abrir Caixa',
      icon: Add,
      onClick: () => setIsOpenSessionModalOpen(true),
    });
  }

  if (openCashSession) {
    if (canViewDetails) {
      actions.push({
        key: 'view',
        label: 'Visualizar',
        icon: Visibility,
        onClick: () => {
          setSelectedCashSessionId(openCashSession.id);
          setIsDetailModalOpen(true);
        },
      });
    }

    if (canCreateManualMovement) {
      actions.push({
        key: 'manual-movement',
        label: 'Movimentar',
        icon: SwapHoriz,
        onClick: () => setIsManualMovementModalOpen(true),
      });
    }

    if (canReconcile) {
      actions.push({
        key: 'start-reconciliation',
        label: 'Conferir',
        icon: AssignmentTurnedIn,
        onClick: () => {
          setSelectedCashSessionId(openCashSession.id);
          setConfirmAction('start-reconciliation');
        },
      });
    }
  }

  const isMutationLoading =
    cashSession.openCashSessionMutation.isLoading ||
    cashSession.createManualMovementMutation.isLoading ||
    cashSession.cancelManualMovementMutation.isLoading ||
    cashSession.startReconciliationMutation.isLoading ||
    cashSession.updateDeclarationsMutation.isLoading ||
    cashSession.closeCashSessionMutation.isLoading ||
    isPrintingSummary ||
    isPrintingManualMovement;
  const isPrintConfirmLoading =
    isPrinting || isPrintingSummary || isPrintingManualMovement;
  const isButtonDisabled =
    currentCashSessionQuery.isLoading || isMutationLoading;

  if (!actions.length) return null;

  return (
    <>
      <Tooltip
        title={
          isOpenedBeforeToday
            ? 'Caixa aberto em DATA ANTERIOR. Verifique antes de continuar.'
            : ''
        }
      >
        <Button
          variant="outlined"
          color={buttonColor}
          endIcon={<KeyboardArrowDown />}
          aria-controls={isMenuOpen ? 'cash-session-actions-menu' : undefined}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen ? 'true' : undefined}
          disabled={isButtonDisabled}
          onClick={handleOpenMenu}
          sx={{ textTransform: 'none', py: 0.75 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <StatusIcon color={statusIconColor} fontSize="small" />
            <Stack spacing={0} alignItems="flex-start" lineHeight={1}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                lineHeight={1.1}
                textTransform="uppercase"
              >
                {label}
              </Typography>
              {openCashSession?.openedAt ? (
                <Typography variant="caption" lineHeight={1.1}>
                  {openCashSession.openedAt.split(',')[0]}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </Button>
      </Tooltip>
      <Menu
        id="cash-session-actions-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        {actions.map((action) => {
          const Icon = action.icon;

          return (
            <MenuItem
              key={action.key}
              onClick={() => {
                action.onClick();
                handleCloseMenu();
              }}
            >
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{action.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>

      <OpenCashSessionModal
        isOpen={isOpenSessionModalOpen}
        isLoading={cashSession.openCashSessionMutation.isLoading}
        onClose={() => setIsOpenSessionModalOpen(false)}
        onSubmit={handleOpenCashSession}
      />
      <CashSessionDetailModal
        isOpen={isDetailModalOpen}
        isLoading={cashSessionDetailQuery.isLoading}
        cashSession={detailCashSession}
        canCreateManualMovement={canCreateManualMovement}
        canCancelManualMovement={canCancelManualMovement}
        canPrintSummary={canPrintSummary}
        isPrintingSummary={isPrintingSummary}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedCashSessionId(undefined);
        }}
        onOpenManualMovement={() => setIsManualMovementModalOpen(true)}
        onCancelManualMovement={(id) => {
          setManualMovementTransactionId(id);
          setConfirmAction('cancel-manual-movement');
        }}
        onPrintSummary={printCashSessionSummary}
      />
      {printSummaryNode}
      {printManualMovementNode}
      <ManualMovementModal
        isOpen={isManualMovementModalOpen}
        isLoading={cashSession.createManualMovementMutation.isLoading}
        documentTypes={documentTypesQuery.data?.documentTypes || []}
        defaultDocumentTypeId={
          settingsQuery.data?.settings.cashDocumentTypeId || ''
        }
        onClose={() => setIsManualMovementModalOpen(false)}
        onSubmit={handleCreateManualMovement}
      />
      <ReconciliationDeclarationsModal
        isOpen={isDeclarationsModalOpen}
        isLoading={
          cashSessionDetailQuery.isFetching ||
          cashSession.updateDeclarationsMutation.isLoading
        }
        cashSession={declarationsCashSession}
        documentTypes={documentTypesQuery.data?.documentTypes || []}
        onClose={() => {
          setIsDeclarationsModalOpen(false);
          setReconciliationCashSession(undefined);
        }}
        onSubmit={handleSaveDeclarations}
      />
      <ConfirmModal
        isOpen={!!confirmAction}
        isLoading={isMutationLoading}
        variant={
          confirmAction
            ? cashSessionConfirmModalConfig[confirmAction]
            : undefined
        }
        title={
          confirmAction
            ? cashSessionConfirmActionConfig[confirmAction].title
            : undefined
        }
        message={
          confirmAction
            ? cashSessionConfirmActionConfig[confirmAction].message
            : undefined
        }
        confirmButtonText={
          confirmAction
            ? cashSessionConfirmActionConfig[confirmAction].confirmButtonText
            : undefined
        }
        onClose={() => {
          setConfirmAction(undefined);
          setManualMovementTransactionId(undefined);
        }}
        onConfirm={handleConfirmAction}
      />
      <ConfirmPrintModal
        isOpen={!!printConfirmAction}
        isLoading={isPrintConfirmLoading}
        title="Imprimir?"
        message={
          printConfirmAction === 'manual-movement'
            ? 'Deseja imprimir esta movimentação manual?'
            : 'Deseja imprimir o resumo do caixa em bobina?'
        }
        onClose={handleClosePrintConfirm}
        onConfirm={handleConfirmPrint}
        confirmButtonText={
          printConfirmAction === 'closed-summary' ? 'Imprimir' : undefined
        }
        onIntentConfirm={
          printConfirmAction === 'closed-summary'
            ? () => handleConfirmPrint('intent')
            : undefined
        }
        onLayoutChange={handleLayoutChange}
        onCopiesChange={handleCopiesChange}
        copies={copies}
        selectedLayout={selectedLayout}
        showLayoutSelector={printConfirmAction !== 'closed-summary'}
        showPrintWidthSelector={printConfirmAction === 'closed-summary'}
        selectedPrintWidth={selectedPrintWidth}
        onPrintWidthChange={handlePrintWidthChange}
      />
    </>
  );
}
